import { ChangeDetectorRef, Directive, inject, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BreadcrumbService } from './breadcrumb/breadcrumb.service';
import { Observable, Subscription } from 'rxjs';
import { Confirmation, ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthorizationService } from '@service/authorization.service';
import { Context, ContextService } from '@service/context.service';
import { Language, LanguageMap, getLocalized } from '@entity/language';
import { DialogService } from 'primeng/dynamicdialog';
import { LoadingDialogComponent } from '@shared/loading-dialog/loading-dialog.component';
import { DynamicDialogRef } from 'primeng/dynamicdialog';

@Directive()
export abstract class AbstractContentComponent implements OnInit, OnDestroy {
  authorizationService = inject(AuthorizationService);
  loading = false;
  protected readonly subscriptions: Subscription[] = [];
  protected dialogService = inject(DialogService);
  protected activatedRoute = inject(ActivatedRoute);
  private translateService = inject(TranslateService);
  private breadcrumbService = inject(BreadcrumbService);
  private contextService = inject(ContextService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private router = inject(Router);
  private loadingDialogRef: DynamicDialogRef | null = null;

  // eslint-disable-next-line @angular-eslint/prefer-inject
  protected constructor(context: Context, breadcrumbLabelKey?: string) {
    if (breadcrumbLabelKey) {
      this.setBreadcrumbs([{ label: breadcrumbLabelKey }]);
    }

    this.contextService.setContext(context);
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.contextService.getAnwenderbetriebId().subscribe((id) => this.onAnwenderbetriebIdChange(id))
    );
    this.subscriptions.push(this.contextService.getMandantId().subscribe((id) => this.onMandantIdChange(id)));
    this.subscriptions.push(this.translateService.onLangChange.subscribe(() => this.onLanguageChange()));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  markForCheck(): void {
    this.changeDetectorRef.markForCheck();
  }

  getAnwenderbetriebId(): number | null {
    return this.contextService.getAnwenderbetriebIdInstant();
  }

  setAnwenderbetriebId(id: number): void {
    return this.contextService.setAnwenderbetriebId(id);
  }

  getMandantId(): number {
    return this.contextService.getMandantIdInstant();
  }

  getMandantLanguages(): Language[] {
    return this.contextService.getMandantLanguages();
  }

  isRodixAdmin(): boolean {
    return this.authorizationService.isRodixAdmin();
  }

  isManager(): boolean {
    return this.authorizationService.isManager();
  }

  isNotRodixAdmin(): boolean {
    return !this.isRodixAdmin();
  }

  startLoading(): void {
    this.loading = true;
    this.markForCheck();
  }

  loadingDone(): void {
    this.loading = false;
    this.markForCheck();
  }

  showLoadingDialog(): void {
    this.loadingDialogRef = this.dialogService.open(LoadingDialogComponent, {
      height: '180px',
      width: '142px',
      closable: false,
      modal: true,
    });
  }

  hideLoadingDialog(): void {
    this.loadingDialogRef?.close();
    this.markForCheck();
  }

  protected translate(key: string, interpolateParams?: object): string {
    return this.translateService.instant(key, interpolateParams);
  }

  protected navigate(commands: unknown[] | string | number, queryParams = {}): void {
    void this.router.navigate(Array.isArray(commands) ? commands : [commands], {
      relativeTo: this.activatedRoute,
      queryParams,
    });
  }

  protected confirm(confirmation: Confirmation): void {
    this.confirmationService.confirm({
      rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
      icon: 'fal fa-exclamation-triangle',
      ...confirmation,
    });
  }

  // `delete` returns null to skip the request (e.g. a missing id), matching callers that guard before deleting.
  protected confirmDelete(options: {
    header: string;
    message: string;
    delete: () => Observable<unknown> | null;
    successMessage: string;
    errorMessage: string;
    onSuccess: () => void;
  }): void {
    this.confirm({
      header: options.header,
      message: options.message,
      accept: () => {
        const request = options.delete();
        if (!request) {
          return;
        }
        request.subscribe({
          next: () => {
            this.showSuccessMessage(options.successMessage);
            options.onSuccess();
          },
          error: () => {
            this.showErrorMessage(options.errorMessage);
          },
        });
      },
    });
  }

  protected setBreadcrumbs(items: MenuItem[]): void {
    this.breadcrumbService.setItems(items);
  }

  protected showSuccessMessage(summary?: string): void {
    this.messageService.add({
      severity: 'success',
      summary: summary ?? this.translate('GLOBAL.SUCCESSFULLY_SAVED'),
    });
    this.markForCheck();
  }

  protected showErrorMessage(summary?: string, detail?: string): void {
    this.messageService.add({
      severity: 'error',
      detail,
      summary: summary ?? this.translate('GLOBAL.NOT_SUCCESSFULLY_SAVED'),
    });
    this.markForCheck();
  }

  protected getLanguage(): string {
    return this.translateService.getCurrentLang() ?? 'de';
  }

  protected getLanguageCaseSensitive(): Language {
    const lang = this.getLanguage();
    return (lang.substring(0, 1).toUpperCase() + lang.substring(1, lang.length).toLowerCase()) as Language;
  }

  protected getLocalizedText(map: LanguageMap | undefined): string {
    return getLocalized(map, this.getLanguageCaseSensitive());
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onAnwenderbetriebIdChange(id: number | null): void {
    // do nothing
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onMandantIdChange(id: number | null): void {
    // do nothing
  }

  protected onLanguageChange(): void {
    // do nothing
  }
}
